import * as React from 'react';
import { useInView } from 'react-intersection-observer';

import { useQuery } from '@apollo/client';

import { IStock } from '@notino/shared/definitions/custom-definitions';
import {
  GetProductDetailAdsQuery,
  GetProductDetailAdsQueryVariables,
} from '@notino/shared/definitions/types';

import { useProductSectionsStateInfo } from '@containers/ProductDetailContainer/useProductSectionsStateInfo';

import { useProductDetailContext } from '../../ProductDetailContext';
import { MAX_SPONSORED_PRODUCTS } from '../SponsoredAdsProducts/constants';

import getProductDetailAdsQuery from './queries/productDetailAds.graphql';
import { flattenCarouselProducts, normalizeProvider } from './tracking';

/**
 * Fetches the product-detail ads carousel (gated on the section scrolling into view
 * and no routine being displayed) and derives the ordered, capped tracking
 * occurrences plus the ids ProductsSection renders.
 */
export const useProductDetailAds = () => {
  const [gateRef, inView] = useInView({ triggerOnce: true });
  const { product } = useProductDetailContext();
  const { isRoutineDisplayed } = useProductSectionsStateInfo();

  const { data, loading } = useQuery<
    GetProductDetailAdsQuery,
    GetProductDetailAdsQueryVariables
  >(getProductDetailAdsQuery, {
    variables: {
      input: {
        masterId: Number(product.catalogMasterId),
        products: product.variants.map((variant) => ({
          id: Number(variant.catalogId),
          price: variant.price.value ?? 0,
          isAvailable: variant.stockAvailability.code !== IStock.outOfStock,
        })),
      },
    },
    skip: !inView || isRoutineDisplayed,
    ssr: false,
  });

  const carousel = data?.ProductDetailAds?.productCarousel;
  const provider = normalizeProvider(carousel?.provider);

  const occurrences = React.useMemo(
    () => flattenCarouselProducts(carousel),
    [carousel]
  );
  // Only the first MAX_SPONSORED_PRODUCTS occurrences are actually rendered.
  const cappedOccurrences = React.useMemo(
    () => occurrences.slice(0, MAX_SPONSORED_PRODUCTS),
    [occurrences]
  );
  const sponsoredProductIds = React.useMemo(
    () => cappedOccurrences.map((o) => o.productId),
    [cappedOccurrences]
  );
  const sponsoredProducts = React.useMemo(
    () =>
      cappedOccurrences
        .map((o) => o.vpProduct)
        .filter((p): p is NonNullable<typeof p> => Boolean(p)),
    [cappedOccurrences]
  );

  return {
    gateRef,
    inView,
    loading,
    carousel,
    provider,
    occurrences,
    cappedOccurrences,
    sponsoredProductIds,
    sponsoredProducts,
  };
};
